import React, { useState, useEffect, useRef } from "react";
import { IoIosAddCircleOutline, IoMdClose } from "react-icons/io";
import { SlOptionsVertical } from "react-icons/sl";
import Swal from "sweetalert2";

import {
  ButtonAction,
  MenuTable,
  MenuTableDivider,
  MenuTableItem,
  Table,
  TableData,
  TableHead,
  TableHeader,
  TableRow,
  TableContainer,
} from "../../../components/Table/styles";
import {
  BlueTitle,
  GrayTitle,
  ModalBody,
  ModalBodyContainer,
  ModalContainer,
  ModalContent,
  ModalFooter,
  ModalHeader,
  StyledHR,
  TextError,
  TitleAndButton,
} from "../../../shared/styles/global.styles";
import { Colors } from "../../../shared/colors/colors";

import { InstrucaoBancariaDto } from "../../../shared/dtos/InstrucaoBancaria.dto";
import instrucaoBancariaService from "../../../services/InstrucaoBancariaService";
import {
  ContainerButton,
  MainContainerInstrucaoBancaria,
} from "./InstrucaoBancaria.styles";
import { Label } from "../../../components/Label/styles";
import Button from "../../../components/Button/button";
import { IoCloseSharp } from "react-icons/io5";
import InputComponent from "../../../components/Input/input";
import { Badge, Col, Form, Row } from "react-bootstrap";
import { useAuth } from "../../../AuthContext/AuthContext";
import { ModulosEnum } from "../../../shared/enum/modulos.enum";
import { PermissoesEnum } from "../../../shared/enum/permicao.enum";

const InstrucaoBancaria: React.FC = () => {
  const { checkPermission } = useAuth();
  const editOnly = checkPermission(
    ModulosEnum.INSTRUCOES_BANCARIAS,
    PermissoesEnum.ESCRITA
  );
  const [instrucaoBancaria, setInstrucaoBancaria] = useState<
    InstrucaoBancariaDto[]
  >([]);

  const [newInstrucaoBancaria, setNewInstrucaoBancaria] =
    useState<InstrucaoBancariaDto>({ tipoInstrucao: "", conteudo: "" });

  const [isRequest, setIsRequest] = useState(false);
  const [invalidFields, setInvalidFields] = useState<string[]>([]);
  const [isOpenDropdown, setIsOpenDropdown] = useState<number | null>(null);
  const [showModalNewInstrucaoBancaria, setShowModalNewInstrucaoBancaria] =
    useState<boolean>(false);
  const [nextLine, setNextLine] = useState<boolean>(false);
  const [lineCount, setLineCount] = useState<number>(1);
  const menuRef = useRef<HTMLDivElement>(null);

  const getInstrucaoBancaria = async () => {
    const response = await instrucaoBancariaService.getAllInstrucaoBancaria();
    setInstrucaoBancaria(response);
  };

  const handleSubmit = () => {
    setIsRequest(true);

    const newInvalidFields: string[] = [];

    if (newInstrucaoBancaria.tipoInstrucao === "") {
      newInvalidFields.push("tipoInstrucao");
    }

    if (newInstrucaoBancaria.conteudo === "") {
      newInvalidFields.push("conteudo");
    }

    if (newInvalidFields.length > 0) {
      setInvalidFields(newInvalidFields);

      Swal.fire({
        icon: "error",
        title: "Erro nos campos",
        text: "Por favor, preencha todos os campos obrigatórios.",
        confirmButtonText: "OK",
        allowOutsideClick: false,
      });

      setIsRequest(false);
      return;
    }

    setIsRequest(true);

    Swal.fire({
      title: "Aguarde...",
      text: "Salvando instrução bancária...",
      icon: "info",
      showConfirmButton: false,
      allowOutsideClick: false,
      willOpen: () => {
        Swal.showLoading();
      },
    });

    if (!!newInstrucaoBancaria.id) {
      instrucaoBancariaService
        .updateInstrucaoBancaria(newInstrucaoBancaria.id, newInstrucaoBancaria)
        .then((response) => {
          Swal.close();
          Swal.fire(
            "Sucesso",
            "Instrução bancária atualizada com sucesso!",
            "success"
          );
          getInstrucaoBancaria(); // Update the table after update
        })
        .catch((error) => {
          console.error(
            "[handleUpdate] Erro ao atualizar instrucaoBancaria:",
            error
          );
          Swal.close();
          Swal.fire(
            "Erro",
            error instanceof Error
              ? error.message
              : "Erro desconhecido ao atualizar instrucaoBancaria.",
            "error"
          );
        });
    } else {
      const instrucaoBancariaExists = instrucaoBancaria.find(
        (l) =>
          l.tipoInstrucao.trim().toLowerCase() ===
          newInstrucaoBancaria?.tipoInstrucao.trim().toLowerCase()
      );
      if (instrucaoBancariaExists) {
        Swal.fire(
          "Alerta",
          "Já existe uma instrução com esse tipo de instrução cadastrado.",
          "warning"
        );
        return;
      }
      instrucaoBancariaService
        .createInstrucaoBancaria(newInstrucaoBancaria)
        .then((response) => {
          Swal.close();
          Swal.fire(
            "Sucesso",
            "Instrução bancária criado com sucesso!",
            "success"
          );
          getInstrucaoBancaria(); // Update the table after creation
        })
        .catch((error) => {
          console.error("[handleAdd] Erro ao criar instrucaoBancaria:", error);
          Swal.close();
          Swal.fire(
            "Erro",
            error instanceof Error
              ? error.message
              : "Erro desconhecido ao criar instrucaoBancaria.",
            "error"
          );
        });
    }

    closeNewInstrucaoBancariaModal();
    setIsRequest(false);
  };

  const handleDeleteInstrucaoBancaria = async (id: number) => {
    try {
      Swal.fire({
        title: "Aguarde...",
        text: "Excluindo a forma de pagamento...",
        icon: "info",
        showConfirmButton: false,
        allowOutsideClick: false,
        willOpen: () => {
          Swal.showLoading();
        },
      });

      const result = await Swal.fire({
        title: "Tem certeza?",
        text: "Você realmente deseja excluir esta forma de pagamento? Esta ação não pode ser desfeita.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sim, excluir!",
        cancelButtonText: "Cancelar",
        allowOutsideClick: false,
      });

      if (result.isConfirmed) {
        await instrucaoBancariaService.deleteInstrucaoBancaria(id);
        getInstrucaoBancaria(); // Refresh the list after deletion
        Swal.close();
        Swal.fire(
          "Excluído!",
          "A forma de pagamento foi excluída com sucesso.",
          "success"
        );
      } else {
        Swal.close();
      }
    } catch (error) {
      console.error(`[handleDelete] Erro ao excluir ID ${id}:`, error);
      Swal.close();
      Swal.fire(
        "Erro",
        error instanceof Error
          ? error.message
          : "Erro desconhecido ao excluir item.",
        "error"
      );
    }
  };

  const toggleDropdown = (id: number) => {
    setIsOpenDropdown(id === isOpenDropdown ? null : id);
  };

  const clearError = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    const { name, value } = event.target;

    setInvalidFields((prev) => prev.filter((field) => field !== name));
  };

  const closeNewInstrucaoBancariaModal = () => {
    setNewInstrucaoBancaria({ tipoInstrucao: "", conteudo: "" });
    setShowModalNewInstrucaoBancaria(false);
    setIsRequest(false);
  };

  useEffect(() => {
    getInstrucaoBancaria();
  }, []);

  const menuRefsMap = useRef<Map<number, HTMLDivElement | null>>(new Map());
  return (
    <MainContainerInstrucaoBancaria>
      <TitleAndButton>
        <GrayTitle>Instruções bancárias</GrayTitle>
        <ContainerButton>
          {editOnly && (
            <Button
              schemaButton="full-primary"
              action={() => setShowModalNewInstrucaoBancaria(true)}
              icon={
                <IoIosAddCircleOutline
                  style={{ fontSize: "1.3rem", marginRight: "0.3rem" }}
                />
              }
              disabled={isRequest && !editOnly}
            >
              Criar nova instrução bancária
            </Button>
          )}
        </ContainerButton>
      </TitleAndButton>
      <StyledHR />

      <TableContainer style={{ flex: 1 }}>
        <Table id="instrucaoBancaria-table">
          <TableHead>
            <TableRow>
              <TableHeader style={{ width: "200px", whiteSpace: "nowrap" }}>
                Tipo de instrução
              </TableHeader>
              <TableHeader>Conteúdo</TableHeader>
              {editOnly && (
                <TableHeader style={{ width: "1px", whiteSpace: "nowrap" }}>
                  Ações
                </TableHeader>
              )}
            </TableRow>
          </TableHead>
          <tbody>
            {instrucaoBancaria.length > 0 ? (
              instrucaoBancaria.map((instrucaoBancariaData) => (
                <TableRow key={instrucaoBancariaData.id}>
                  <TableData>{instrucaoBancariaData.tipoInstrucao}</TableData>
                  <TableData>{instrucaoBancariaData.conteudo}</TableData>
                  {editOnly && (
                    <TableData
                      key={`actions-${instrucaoBancariaData.id}`}
                      style={{
                        position: "relative",
                        overflow: "visible",
                      }}
                    >
                      <ButtonAction
                        onClick={() =>
                          toggleDropdown(instrucaoBancariaData.id!)
                        }
                      >
                        {isOpenDropdown === instrucaoBancariaData.id ? (
                          <IoMdClose
                            cursor={"pointer"}
                            size={20}
                            style={{ color: Colors.primary }}
                          />
                        ) : (
                          <SlOptionsVertical
                            cursor={"pointer"}
                            size={20}
                            style={{ color: Colors.primary }}
                          />
                        )}
                      </ButtonAction>
                      <MenuTable
                        isLastRows={
                          instrucaoBancaria.length > 0 &&
                          instrucaoBancaria.indexOf(instrucaoBancariaData) >=
                            instrucaoBancaria.length -
                              Math.ceil(instrucaoBancaria.length / 2)
                        }
                        ref={(el: HTMLDivElement | null) => {
                          // Armazena a referência no mapa usando o ID do registro como chave
                          menuRefsMap.current.set(
                            instrucaoBancariaData.id!,
                            el
                          );
                        }}
                        show={isOpenDropdown === instrucaoBancariaData.id}
                        align="start"
                      >
                        <MenuTableItem
                          eventKey="1"
                          onClick={() => {
                            setNewInstrucaoBancaria(instrucaoBancariaData);
                            setIsOpenDropdown(null);
                            setShowModalNewInstrucaoBancaria(true);
                          }}
                        >
                          Editar
                        </MenuTableItem>
                        <MenuTableDivider />
                        <MenuTableItem
                          eventKey="2"
                          onClick={() => {
                            handleDeleteInstrucaoBancaria(
                              instrucaoBancariaData.id!
                            );
                            setIsOpenDropdown(null);
                          }}
                        >
                          Excluir
                        </MenuTableItem>
                      </MenuTable>
                    </TableData>
                  )}
                </TableRow>
              ))
            ) : (
              <tr>
                <TableData colSpan={3} className="text-center">
                  Nenhuma instrução bancária encontrada.
                </TableData>
              </tr>
            )}
          </tbody>
        </Table>
      </TableContainer>

      <ModalContainer show={showModalNewInstrucaoBancaria}>
        <ModalContent width={65}>
          <Button
            type="button"
            schemaButton="x-close"
            action={() => closeNewInstrucaoBancariaModal()}
            icon={<IoCloseSharp size={20} />}
            position="absolute"
            top={10}
            right={10}
          />
          <ModalHeader>
            <BlueTitle>
              {!!newInstrucaoBancaria.id ? "Editar " : "Cadastrar nova "}
              instrução bancária
            </BlueTitle>
          </ModalHeader>

          <ModalBody>
            <Label htmlFor="tipoRemessa">
              Tipo de instrução <TextError>*</TextError>
            </Label>
            <InputComponent
              type="text"
              name="tipoInstrucao"
              id="tipoInstrucao"
              value={newInstrucaoBancaria.tipoInstrucao ?? ""}
              onChange={(e) => {
                setNewInstrucaoBancaria({
                  ...newInstrucaoBancaria,
                  tipoInstrucao: e.target.value,
                });
                clearError(e);
              }}
              placeholder="Tipo de instrução"
              disabled={isRequest}
              color="black"
              width={Number("100%")}
              errorMessage={
                invalidFields.includes("tipoInstrucao")
                  ? "O tipo de instrução é obrigatório"
                  : ""
              }
            />
            <Label htmlFor="tipoRemessa">
              Conteúdo <TextError>*</TextError>
            </Label>
            <div>
              <Form.Control
                as="textarea"
                style={{
                  minHeight: "100px",
                  maxHeight: "300px",
                  overflow: "auto",
                  resize: "none",
                }}
                placeholder="Conteúdo (máximo 7 linhas, cada linha com recomendação de 90 caracteres)"
                name="conteudo"
                id="conteudo"
                value={newInstrucaoBancaria.conteudo ?? ""}
                onChange={(e) => {
                  const value = e.target.value;
                  const lines = value.split("\n");
                  if (lines.length > 7) {
                    e.target.value = value.substring(
                      0,
                      value.lastIndexOf("\n")
                    );
                    return setLineCount(lines.length);
                  }
                  // const lastLine = lines[lines.length - 1];
                  // if (lastLine.length > 90) {
                  //   e.target.value = value.substring(0, value.length - 1);
                  //   return setNextLine(true);
                  // }
                  // setNextLine(false);
                  setLineCount(lines.length);
                  setNewInstrucaoBancaria({
                    ...newInstrucaoBancaria,
                    conteudo: value,
                  });
                  clearError({
                    target: { name: e.target.name, value },
                  } as React.ChangeEvent<HTMLInputElement>);
                }}
                disabled={isRequest}
                rows={8}
                isInvalid={invalidFields.includes("conteudo")}
                maxLength={800}
              />
              <div>
                <Row style={{ justifyContent: "start" }}>
                  {newInstrucaoBancaria.conteudo
                    ?.split("\n")
                    .slice(0, 4)
                    .map((line, index) => (
                      <Col key={index} xs="auto">
                        <Badge
                          bg={
                            newInstrucaoBancaria.conteudo?.split("\n")
                              .length ===
                            index + 1
                              ? "primary"
                              : line.length > 90
                              ? "dark"
                              : "secondary"
                          }
                        >
                          Linha{" "}
                          {`${index + 1} de 7 - ${
                            line.length === 91 ? "90" : line.length
                          }/90 caracteres`}
                        </Badge>
                      </Col>
                    ))}
                </Row>
                <Row style={{ justifyContent: "start" }}>
                  {newInstrucaoBancaria.conteudo
                    ?.split("\n")
                    .slice(4, 7)
                    .map((line, index) => (
                      <Col key={index + 5} xs="auto">
                        <Badge
                          bg={
                            newInstrucaoBancaria.conteudo?.split("\n")
                              .length ===
                            index + 5
                              ? "primary"
                              : line.length > 90
                              ? "dark"
                              : "secondary"
                          }
                        >
                          Linha{" "}
                          {`${index + 5} de 7 - ${line.length}/90 caracteres`}
                        </Badge>
                      </Col>
                    ))}
                </Row>
              </div>
            </div>
            {/* {nextLine && lineCount < 7 ? (
              <TextError>
                Pule para próxima linha, limite de 90 caracteres por linha
              </TextError>
            ) : (
              nextLine &&
              lineCount >= 7 && (
                <TextError>Limite para conteúdo atingido</TextError>
              )
            )} */}
            {lineCount >= 7 && (
              <TextError>Limite de 7 linhas atingido</TextError>
            )}
            {invalidFields.includes("conteudo") && (
              <TextError>O conteúdo é obrigatória</TextError>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              schemaButton="full-primary"
              action={() => {
                handleSubmit();
              }}
              disabled={isRequest}
            >
              {!!newInstrucaoBancaria.id ? "Editar" : "Cadastrar"} instrução
              bancária
            </Button>
          </ModalFooter>
        </ModalContent>
      </ModalContainer>
    </MainContainerInstrucaoBancaria>
  );
};

export default InstrucaoBancaria;
